// 9 Oct: proof of identity for a returning player. The email is the identity, so the saved details and the record come
// back only once the address is proved: a sign-in with a code. What the player screens need for that: whether to offer
// sign-in (the person icon), where sign-in goes and comes back to, and the signed-in session on this device that can
// prove the joined address. The joined address is the one this device remembered at the join (lib/session's loadMe);
// the game's own address (token, code, player, name) travels in `next`, so the player lands back in the battle even if
// Instagram's browser wiped its storage on the way. Nothing here logs.
import type { MeResponse } from '@shared/types';
import type { StaffSession } from '@/staff/session';
import { loadMe, type Session } from './session';

/** The address this device joined with, as the server compares it; undefined when nothing is remembered. */
export function joinedEmail(): string | undefined {
  const e = loadMe()?.email.trim().toLowerCase();
  return e || undefined;
}

/** Whether the game should offer sign-in: the server says the address has played before and it is not yet proved. */
/**
 * 14 Oct: account offers to players (the sign-in icon for a returning address, and "Keep your results" on the result
 * page) are off until the user pool sends email through SES: Cognito's own sender allows 50 emails a day, far short of a
 * 500-player stream, so most taps would end in a code that never comes. Set `on` back to true with SES. Tests switch it on.
 */
export const ACCOUNT_OFFERS = { on: false };
export const offersProof = (me: MeResponse | undefined): boolean => ACCOUNT_OFFERS.on && Boolean(me?.returning?.known) && me?.verified !== true;

/** Where the sheet is: the game, the end-of-battle details step (9 Oct), or the result page. */
export type ProofWhere = 'play' | 'details' | 'result';
const PAGE: Record<ProofWhere, string> = { play: '/play', details: '/details', result: '/r' };

/** The page's own address, carrying this player's place (the session in the query, which every one of these pages reads): where sign-in comes back to. */
export function returnPath(s: Session, where: ProofWhere = 'play'): string {
  const q = new URLSearchParams({ t: s.token, c: s.code, p: s.playerId, n: s.name });
  return `${PAGE[where]}?${q.toString()}`;
}

/** The sign-in page with an address filled in (when one is known) and the way back to `next`. */
export function signInWithAddress(email: string | undefined, next: string): string {
  const q = new URLSearchParams();
  if (email) q.set('email', email);
  q.set('next', next);
  return `/sign-in?${q.toString()}`;
}

/** The sign-in page with the joined address filled in (when this device remembers it) and the way back to the page. */
export function signInForProof(s: Session, where: ProofWhere = 'play', email: string | undefined = joinedEmail()): string {
  return signInWithAddress(email, returnPath(s, where));
}

/**
 * The signed-in session on this device that can prove the joined address: the one whose address matches, or, when no
 * address is remembered (storage wiped or refused), whichever is signed in. The server is the authority: a session for
 * another address costs one refusal, which is not repeated for that sign-in.
 */
export function proofSession(account: StaffSession | undefined, email: string | undefined = joinedEmail()): StaffSession | undefined {
  if (!account) return undefined;
  return !email || account.email.trim().toLowerCase() === email ? account : undefined;
}

/** POST here, with the player's session, to prove the address with a sign-in's ID token. */
export const verifyPath = (code: string) => `/battles/${code}/verify`;
